import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import type { Plan } from '@brix/types'
import { usePlatformAdminTenantDetail, useClearTenantCustomPlan } from '@/hooks/usePlatformAdmin'
import { SetCustomPlanModal } from './SetCustomPlanModal'

const FALLBACK_PLANS: Exclude<Plan, 'custom'>[] = ['free', 'starter', 'pro', 'enterprise']

export function PlatformAdminTenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const { data, isLoading, error } = usePlatformAdminTenantDetail(tenantId)
  const [showPlanModal, setShowPlanModal] = useState(false)
  const clearCustomPlan = useClearTenantCustomPlan()

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load tenant: {error instanceof Error ? error.message : 'not found'}
      </div>
    )
  }

  const { tenant, subscription, sites, members } = data

  return (
    <div>
      <Link to="/platform-admin" className="mb-4 flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" />
        All tenants
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
        <p className="mt-1 text-sm text-gray-500">{tenant.slug} · plan: {tenant.plan} · created {new Date(tenant.created_at).toLocaleDateString()}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Plan */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white lg:col-span-2">
          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Plan
            {tenant.plan === 'custom' ? (
              <div className="flex gap-3 normal-case tracking-normal">
                <button className="font-medium text-violet-600 hover:text-violet-700" onClick={() => setShowPlanModal(true)}>
                  Edit
                </button>
                <select
                  className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs text-gray-700"
                  disabled={clearCustomPlan.isPending}
                  defaultValue=""
                  onChange={(e) => {
                    const fallback = e.target.value as Exclude<Plan, 'custom'>
                    if (fallback && tenantId) clearCustomPlan.mutate({ tenantId, fallbackPlan: fallback })
                  }}
                >
                  <option value="" disabled>Revert to…</option>
                  {FALLBACK_PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            ) : (
              <button className="font-medium text-violet-600 normal-case tracking-normal hover:text-violet-700" onClick={() => setShowPlanModal(true)}>
                Assign custom plan
              </button>
            )}
          </div>
          <div className="p-4 text-sm">
            {tenant.plan === 'custom' && tenant.custom_limits ? (
              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Row label="Sites" value={String(tenant.custom_limits.siteLimit)} />
                <Row label="Pages/site" value={String(tenant.custom_limits.pageLimit)} />
                <Row label="Posts/site" value={String(tenant.custom_limits.postLimit)} />
                <Row label="Analytics" value={tenant.custom_limits.canViewAnalytics ? 'Yes' : 'No'} />
                <Row label="AI assistant" value={tenant.custom_limits.canUseAiAssistant ? 'Yes' : 'No'} />
                <Row label="Pro blocks" value={tenant.custom_limits.canUseProBlocks ? 'Yes' : 'No'} />
              </dl>
            ) : (
              <p className="text-gray-400">On plan "{tenant.plan}" — one of the 4 fixed tiers.</p>
            )}
          </div>
        </section>

        {/* Subscription */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Subscription
          </div>
          <div className="p-4 text-sm">
            {subscription ? (
              <dl className="space-y-2">
                <Row label="Status" value={subscription.status} />
                <Row label="Plan" value={subscription.plan} />
                <Row label="Renews / ends" value={subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : '—'} />
                <Row label="Cancels at period end" value={subscription.cancel_at_period_end ? 'Yes' : 'No'} />
                <Row label="Stripe customer" value={subscription.stripe_customer_id ?? '—'} />
              </dl>
            ) : (
              <p className="text-gray-400">No subscription on record — free plan.</p>
            )}
          </div>
        </section>

        {/* Members */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Members ({members.length})
          </div>
          <ul className="divide-y divide-gray-100">
            {members.map((m) => (
              <li key={m.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <div>
                  <p className="font-medium text-gray-900">{m.name ?? m.email}</p>
                  <p className="text-xs text-gray-400">{m.email}</p>
                </div>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{m.role}</span>
              </li>
            ))}
            {members.length === 0 && <li className="px-4 py-6 text-center text-sm text-gray-400">No members</li>}
          </ul>
        </section>

        {/* Sites */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white lg:col-span-2">
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Sites ({sites.length})
          </div>
          <ul className="divide-y divide-gray-100">
            {sites.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <div>
                  <p className="font-medium text-gray-900">{s.name}</p>
                  <p className="text-xs text-gray-400">{s.subdomain}{s.customDomain ? ` · ${s.customDomain}` : ''}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${s.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {s.published ? 'Published' : 'Draft'}
                  </span>
                  {s.published && (
                    <a
                      href={`https://${s.subdomain}.brix.app`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-gray-400 hover:text-violet-600"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </li>
            ))}
            {sites.length === 0 && <li className="px-4 py-6 text-center text-sm text-gray-400">No sites</li>}
          </ul>
        </section>
      </div>

      {showPlanModal && tenantId && (
        <SetCustomPlanModal
          tenantId={tenantId}
          initialLimits={tenant.plan === 'custom' ? tenant.custom_limits : null}
          onClose={() => setShowPlanModal(false)}
        />
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900">{value}</dd>
    </div>
  )
}
